import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Info, RotateCcw, Pencil, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import { PROJECT_STATUS_ID_MAPPING, STATUS_COLORS } from '@/lib/status-colors';

// Only the statuses available in the system's status picker
// IDs 4 (Reopened) and 7 (Opportunity) are not user-selectable
const AVAILABLE_STATUS_IDS = [1, 2, 3, 5, 6] as const;

const DEFAULT_STATUS_LABELS: Record<number, string> = {
  1: 'Planning',
  2: 'In Progress',
  3: 'On Hold',
  5: 'Cancelled',
  6: 'Completed',
};

export interface ProjectStatusLabelsHandle {
  save: () => Promise<void>;
  discard: () => void;
}

interface ProjectStatusLabelsProps {
  onDirtyChange?: (dirty: boolean) => void;
}

export const ProjectStatusLabels = forwardRef<ProjectStatusLabelsHandle, ProjectStatusLabelsProps>(
  function ProjectStatusLabels({ onDirtyChange }, ref) {
  const { statuses: projectStatuses, updateStatusLabel, isLoading: isLoadingStatuses } = useProjectStatuses();
  const [statusEdits, setStatusEdits] = useState<Record<number, string>>({});

  // Sync statusEdits when projectStatuses load/change
  useEffect(() => {
    if (projectStatuses.length > 0 && Object.keys(statusEdits).length === 0) {
      const edits: Record<number, string> = {};
      projectStatuses.forEach(s => { edits[s.id] = s.name; });
      setStatusEdits(edits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectStatuses]);

  const hasUnsavedChanges = projectStatuses.some(
    s => statusEdits[s.id] !== undefined && statusEdits[s.id] !== s.name
  );

  const handleStatusNameChange = (id: number, value: string) => {
    setStatusEdits(prev => ({ ...prev, [id]: value }));
  };

  const save = useCallback(async () => {
    if (Object.values(statusEdits).some(v => !v || v.trim() === '')) {
      throw new Error('Status labels cannot be empty.');
    }
    const changedStatuses = projectStatuses.filter(
      s => statusEdits[s.id] !== undefined && statusEdits[s.id] !== s.name
    );
    for (const status of changedStatuses) {
      await updateStatusLabel(status.id, statusEdits[status.id]);
    }
  }, [statusEdits, projectStatuses, updateStatusLabel]);

  const discard = useCallback(() => {
    const edits: Record<number, string> = {};
    projectStatuses.forEach(s => { edits[s.id] = s.name; });
    setStatusEdits(edits);
  }, [projectStatuses]);

  useImperativeHandle(ref, () => ({ save, discard }), [save, discard]);

  useEffect(() => { onDirtyChange?.(hasUnsavedChanges); }, [hasUnsavedChanges, onDirtyChange]);

  const handleResetStatusesToDefaults = () => {
    setStatusEdits({ ...DEFAULT_STATUS_LABELS });
    toast.info('Reset to defaults', {
      description: 'All status labels have been reset to their default values.',
    });
  };

  return (
    <div className="space-y-6">
      {hasUnsavedChanges && (
        <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-3">
          <Info className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            You have unsaved changes — use Save at the bottom of the page to apply them.
          </span>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5" />
                Project Status Labels
              </CardTitle>
              <CardDescription>
                Customise the display names for each project status. These labels will be used throughout the application.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetStatusesToDefaults}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingStatuses ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">Loading status labels...</span>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {AVAILABLE_STATUS_IDS.map(id => {
                  const statusType = PROJECT_STATUS_ID_MAPPING[id] || 'pending';
                  const colorClasses = STATUS_COLORS[statusType];
                  const currentValue = statusEdits[id] ?? projectStatuses.find(s => s.id === id)?.name ?? DEFAULT_STATUS_LABELS[id];
                  const hasUnsavedEdit = projectStatuses.find(s => s.id === id)?.name !== currentValue;
                  const isModifiedFromDefault = currentValue !== DEFAULT_STATUS_LABELS[id];
                  const isEmpty = !currentValue || currentValue.trim() === '';
                  const isTooLong = currentValue && currentValue.length > 50;

                  return (
                    <div key={id} className="flex items-center gap-4">
                      <Badge className={`${colorClasses.badge} min-w-[100px] justify-center text-xs`}>
                        {currentValue || DEFAULT_STATUS_LABELS[id]}
                      </Badge>
                      <div className="flex-1">
                        <Input
                          value={currentValue}
                          onChange={(e) => handleStatusNameChange(id, e.target.value)}
                          placeholder={DEFAULT_STATUS_LABELS[id]}
                          maxLength={50}
                          className={`${isEmpty || isTooLong ? 'border-destructive' : ''} ${hasUnsavedEdit ? 'border-primary' : ''}`}
                        />
                      </div>
                      {isModifiedFromDefault && (
                        <span className="text-xs text-muted-foreground italic shrink-0">Modified</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {Object.values(statusEdits).some(v => !v || v.trim() === '') && (
                <div className="flex items-center gap-2 mt-4 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  Status labels cannot be empty.
                </div>
              )}
              <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                <Info className="h-4 w-4" />
                Changes to status labels affect all users in your organisation.
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
});
